<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceEntries.vue.
  Kept: collapse-all, clear selection, entry rows, cross-entry group, actions menu (Reset / Remove components),
  more menu (Rename / Delete). The `actions` slot (header row, after Clear selection) carries undo/redo in the panel. Dropped: align, interactions, export, DX map, trajectory, playback (server/collab).
-->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { useLiteStore, type Entry } from '@nanome-lite/state';
import { useConfirmMenuItem } from '@/composables/confirmMenuItem';
import EntryRow from './EntryRow.vue';
import EntryComponents from './EntryComponents.vue';
import RenamePrompt from './RenamePrompt.vue';
import EmptyResult from './EmptyResult.vue';

const store = useLiteStore();

const CROSS_ENTRY_KEY = 'cross-entry';
const collapsedRows = ref<Record<string, boolean>>({});
const ctxEntry = ref<Entry | null>(null);
const ctxEntryEl = ref<EventTarget | null>(null);
const menuActions = ref<InstanceType<typeof Menu>>();
const menuMore = ref<InstanceType<typeof Menu>>();
const panelRename = ref<InstanceType<typeof RenamePrompt>>();

const entries = computed(() => store.entries);
const crossEntryComponents = computed(() => store.crossEntryComponents);

const entryHiddenStates = computed(() => {
  const states = entries.value.map((entry) => {
    const cs = store.componentsForEntry(entry.serial);
    return { serial: String(entry.serial), allHidden: cs.length === 0 || cs.every(c => c.hidden) };
  });
  if (crossEntryComponents.value.length) states.push({ serial: CROSS_ENTRY_KEY, allHidden: crossEntryComponents.value.every(c => c.hidden) });
  return states;
});

// rows collapse when all their components become hidden and expand again when one becomes visible
watch(entryHiddenStates, (newStates, oldStates) => {
  for (const { serial, allHidden } of newStates) {
    const wasAllHidden = oldStates?.find(s => s.serial === serial)?.allHidden;
    if (allHidden && !wasAllHidden) collapsedRows.value[serial] = true;
    else if (!allHidden && wasAllHidden) delete collapsedRows.value[serial];
  }
}, { immediate: true });

const menuActionsItems = computed<MenuItem[]>(() => [
  { label: 'Reset components', icon: 'i-tabler-refresh', command: () => { if (ctxEntry.value) store.resetDefaultComponents(ctxEntry.value.serial); } },
  {
    label: 'Remove components', icon: 'i-tabler-eraser',
    disabled: !ctxEntry.value || !store.componentsForEntry(ctxEntry.value.serial).length,
    command: () => { if (ctxEntry.value) store.removeAllComponents(ctxEntry.value.serial); },
  },
]);

const { menuItem: deleteMenuItem } = useConfirmMenuItem({
  menu: menuMore,
  onConfirm() { if (ctxEntry.value) store.removeEntry(ctxEntry.value.serial); },
});

const menuMoreItems = computed<MenuItem[]>(() => [
  { label: 'Rename', icon: 'i-tabler-pencil', command: () => togglePanelRename() },
  deleteMenuItem.value,
]);

function renameItem(value: string) { if (ctxEntry.value) store.renameEntry(ctxEntry.value.serial, value); }

function toggleCollapsedAllRows() {
  if (Object.keys(collapsedRows.value).length > 0) collapsedRows.value = {};
  else collapsedRows.value = Object.fromEntries([
    ...entries.value.map(e => [String(e.serial), true]),
    ...(crossEntryComponents.value.length ? [[CROSS_ENTRY_KEY, true]] : []),
  ]);
}

function getCrossEntryEyeIcon() {
  if (crossEntryComponents.value.every(c => c.hidden)) return 'i-tabler-eye-off';
  if (crossEntryComponents.value.some(c => c.hidden)) return 'i-tabler-eye-minus';
  return 'i-tabler-eye';
}

function onCrossEntryRowClick(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('button')) return;
  store.toggleComponentsSelection(crossEntryComponents.value.map(c => c.serial));
}

function toggleCollapsedRow(key: string) {
  if (collapsedRows.value[key]) delete collapsedRows.value[key];
  else collapsedRows.value[key] = true;
}

async function toggleMenuActions(e: Event, entry: Entry) {
  menuActions.value?.hide();
  if (ctxEntryEl.value === e.currentTarget) return;
  await nextTick();
  ctxEntryEl.value = e.currentTarget;
  ctxEntry.value = entry;
  panelRename.value?.hide();
  menuActions.value?.show(e);
}

async function toggleMenuMore(e: Event, entry: Entry) {
  menuMore.value?.hide();
  panelRename.value?.hide();
  if (ctxEntryEl.value === e.currentTarget) return;
  await nextTick();
  ctxEntryEl.value = e.currentTarget;
  ctxEntry.value = entry;
  menuMore.value?.show(e);
}

function togglePanelRename() {
  if (!ctxEntry.value) return;
  const currentTarget = ctxEntryEl.value;
  const name = ctxEntry.value.name;
  setTimeout(() => panelRename.value?.show(currentTarget, name));
}
</script>

<template>
  <EmptyResult v-if="entries.length === 0" item="entries" icon="i-tabler-file" />

  <div v-else class="workspace-entries flex flex-col gap-2">
    <div class="h-9 flex items-center gap-2">
      <Button
        v-if="entries.length > 1 || crossEntryComponents.length"
        :icon="Object.keys(collapsedRows).length > 0 ? 'i-tabler-chevron-right' : 'i-tabler-chevron-down'"
        severity="secondary"
        size="small"
        rounded
        @click="toggleCollapsedAllRows"
      />
      <div class="ml-auto flex items-center gap-2">
        <Button
          v-if="store.hasSelection"
          icon="i-tabler-deselect"
          label="Clear selection"
          severity="secondary"
          size="small"
          rounded
          @click="store.clearSelection()"
        />
        <slot name="actions" />
      </div>
    </div>

    <div class="-mx-4 flex flex-col ws-nowrap">
      <EntryRow
        v-for="entry in entries"
        :key="entry.serial"
        :entry="entry"
        :collapsed="!!collapsedRows[String(entry.serial)]"
        @toggle-collapse="toggleCollapsedRow(String($event.serial))"
        @toggle-actions="toggleMenuActions"
        @toggle-more="toggleMenuMore"
      />

      <div v-if="crossEntryComponents.length" class="flex flex-col">
        <div
          :data-selection="store.componentSelectionState(crossEntryComponents.map(c => c.serial))"
          class="selectable-row flex items-center gap-2 px-4 py-1"
          @click="onCrossEntryRowClick"
        >
          <Button
            :icon="collapsedRows[CROSS_ENTRY_KEY] ? 'i-tabler-chevron-right' : 'i-tabler-chevron-down'"
            severity="secondary"
            size="small"
            rounded
            @click="toggleCollapsedRow(CROSS_ENTRY_KEY)"
          />
          <Button
            :icon="getCrossEntryEyeIcon()"
            severity="secondary"
            size="small"
            rounded
            @click="store.toggleHidden(crossEntryComponents.map(c => c.serial), !crossEntryComponents.every(c => c.hidden))"
          />
          <span :class="{ 'op-25': crossEntryComponents.every(c => c.hidden) }" class="flex-1 truncate">
            Cross-Entry Components
          </span>
        </div>
        <div v-if="!collapsedRows[CROSS_ENTRY_KEY]">
          <EntryComponents :entry-serial="null" entry-name="Cross-Entry Components" />
        </div>
      </div>
    </div>
  </div>

  <Menu ref="menuActions" :model="menuActionsItems" popup @hide="ctxEntryEl = null" />
  <Menu ref="menuMore" :model="menuMoreItems" popup @hide="ctxEntryEl = null" />
  <RenamePrompt ref="panelRename" type="entry" @rename="renameItem" />
</template>
